/**
 * Review author avatars + screen names.
 *
 * Public faces on this marketplace live on the Listening/Talking listing
 * (title + images), not the account First-L displayName. Account profileImage
 * is used when present. Reviews render a plain <img> (v459/v461 thumbs) so we
 * never import ResponsiveImage through the components barrel.
 */

import { createImageVariantConfig } from './sdkLoader';
import { IMAGE_FIELD_VARIANTS } from './avatarImage';
import { denormalisedEntities, updatedEntities } from './data';

export const REVIEW_AUTHOR_IMAGE_VARIANTS = ['square-small', 'square-small2x'];

const LISTING_PHOTO_VARIANTS = [
  'square-small',
  'square-small2x',
  'listing-card',
  'listing-card-2x',
  'square-xsmall',
  'square-xsmall2x',
  'scaled-small',
  'scaled-medium',
];

const variantUrl = variant => (typeof variant?.url === 'string' ? variant.url : '');

const entityId = value => {
  if (!value) {
    return '';
  }
  if (typeof value === 'string') {
    return value;
  }
  return value.uuid || value.id || '';
};

const trimmed = value => (typeof value === 'string' ? value.trim() : '');

/**
 * Image-variant query used on every reviews.query so listing covers and
 * profile thumbs arrive with a usable URL.
 *
 * @returns {Object}
 */
export const reviewAuthorImageQuery = () => ({
  include: [
    'author',
    'author.profileImage',
    'listing',
    'listing.images',
    'listing.author',
    'listing.author.profileImage',
  ],
  'fields.image': [
    'variants.square-small',
    'variants.square-small2x',
    'variants.listing-card',
    'variants.listing-card-2x',
    ...IMAGE_FIELD_VARIANTS,
  ],
  ...createImageVariantConfig('square-small', 96, 1),
  ...createImageVariantConfig('square-small2x', 192, 1),
  ...createImageVariantConfig('listing-card', 400, 1),
  ...createImageVariantConfig('listing-card-2x', 800, 1),
});

/**
 * First usable URL on an image entity. Square thumbs first.
 *
 * @param {Object} [image]
 * @returns {string}
 */
export const reviewImageUrl = image => {
  const variants = image?.attributes?.variants;
  if (!variants || typeof variants !== 'object') {
    return typeof image?.attributes?.url === 'string' ? image.attributes.url : '';
  }
  for (let i = 0; i < LISTING_PHOTO_VARIANTS.length; i += 1) {
    const url = variantUrl(variants[LISTING_PHOTO_VARIANTS[i]]);
    if (url) {
      return url;
    }
  }
  const first = Object.keys(variants)
    .map(name => variantUrl(variants[name]))
    .find(Boolean);
  return first || '';
};

/**
 * Pick square-small / square-small2x from a profile image, or any URL it has.
 *
 * @param {Object} [image]
 * @returns {Object|null}
 */
export const clientReviewProfileImage = image => {
  const variants = image?.attributes?.variants;
  if (!image || !variants || typeof variants !== 'object') {
    return null;
  }
  const picked = {};
  REVIEW_AUTHOR_IMAGE_VARIANTS.forEach(name => {
    if (variantUrl(variants[name])) {
      picked[name] = variants[name];
    }
  });
  if (Object.keys(picked).length === 0) {
    Object.keys(variants).forEach(name => {
      if (variantUrl(variants[name])) {
        picked[name] = variants[name];
      }
    });
  }
  if (Object.keys(picked).length === 0) {
    return null;
  }
  return {
    id: image.id,
    type: image.type || 'image',
    attributes: { variants: picked },
  };
};

const listingFromReview = review =>
  review?.listing ||
  review?.author?.profileListing ||
  review?.author?.listing ||
  review?.authorListing ||
  null;

const listingAuthorId = listing =>
  entityId(listing?.author?.id) ||
  entityId(listing?.relationships?.author?.data?.id) ||
  entityId(listing?.author);

/**
 * True when the reviewer owns the listing attached to the review
 * (listener reviewing a talker — listing is their Listening profile).
 *
 * @param {Object} [review]
 * @returns {boolean}
 */
export const authorOwnsReviewListing = review => {
  const listing = listingFromReview(review);
  const authorId = entityId(review?.author?.id);
  const ownerId = listingAuthorId(listing);
  if (!listing || !authorId) {
    return false;
  }
  if (ownerId) {
    return ownerId === authorId;
  }
  // ofCustomer: listener reviewing the talker — listing is the listener’s.
  return review?.attributes?.type === 'ofCustomer';
};

/**
 * Initials from a screen name (Lauren Sophia → LS). Account LJ only if
 * that is the only name we have.
 *
 * @param {string} [name]
 * @returns {string}
 */
export const reviewAuthorInitials = name => {
  const parts = trimmed(name)
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) {
    return '';
  }
  return parts
    .slice(0, 2)
    .map(part => part[0])
    .join('')
    .toUpperCase();
};

/**
 * Reviewer screen name. Listing title when the reviewer owns that listing
 * (Lauren Sophia). Never First L when a listing title is available.
 *
 * @param {Object} [review]
 * @returns {string}
 */
export const reviewAuthorScreenName = review => {
  const author = review?.author;
  const listing = listingFromReview(review);
  const ownTitle = trimmed(
    author?.profileListing?.attributes?.title ||
      author?.listing?.attributes?.title ||
      review?.authorListing?.attributes?.title
  );
  if (ownTitle) {
    return ownTitle;
  }
  const listingTitle = trimmed(listing?.attributes?.title);
  if (listingTitle && authorOwnsReviewListing({ ...review, listing })) {
    return listingTitle;
  }
  const profile = author?.attributes?.profile || {};
  const publicData = profile.publicData || {};
  const fromPublic =
    trimmed(publicData.listeningDisplayName) || trimmed(publicData.talkingDisplayName);
  if (fromPublic) {
    return fromPublic;
  }
  return trimmed(profile.displayName);
};

const firstListingImage = listing => {
  const images = listing?.images;
  if (Array.isArray(images)) {
    return images.find(image => reviewImageUrl(image)) || null;
  }
  return null;
};

/**
 * Reviewer photo URL: account profileImage, then their listing cover.
 *
 * @param {Object} [review]
 * @returns {string}
 */
export const reviewAuthorPhotoUrl = review => {
  const author = review?.author;
  const fromProfile = reviewImageUrl(author?.profileImage);
  if (fromProfile) {
    return fromProfile;
  }
  const ownListing = author?.profileListing || author?.listing || review?.authorListing;
  const fromOwnListing = reviewImageUrl(firstListingImage(ownListing));
  if (fromOwnListing) {
    return fromOwnListing;
  }
  if (authorOwnsReviewListing(review)) {
    const fromReviewListing = reviewImageUrl(firstListingImage(listingFromReview(review)));
    if (fromReviewListing) {
      return fromReviewListing;
    }
  }
  return '';
};

/**
 * Attach a usable profileImage onto a denormalized review author and stamp
 * the listing screen name onto displayName so Avatar fallbacks stay consistent.
 *
 * @param {Object} [review]
 * @returns {Object}
 */
export const hydrateReviewAuthor = review => {
  if (!review?.author) {
    return review;
  }
  const listing = listingFromReview(review);
  const listingImage = authorOwnsReviewListing({ ...review, listing })
    ? firstListingImage(listing)
    : firstListingImage(review.author.profileListing || review.author.listing);
  const profileImage =
    clientReviewProfileImage(review.author.profileImage) || clientReviewProfileImage(listingImage);
  const author = {
    ...review.author,
    profileImage,
    profileListing: review.author.profileListing || (authorOwnsReviewListing(review) ? listing : null),
  };
  const screenName = reviewAuthorScreenName({ ...review, author, listing });
  const profile = author.attributes?.profile || {};
  return {
    ...review,
    listing,
    author: {
      ...author,
      attributes: {
        ...(author.attributes || {}),
        profile: {
          ...profile,
          displayName: screenName || profile.displayName,
          abbreviatedName: reviewAuthorInitials(screenName) || profile.abbreviatedName,
        },
      },
    },
  };
};

/**
 * Denormalize a reviews.query payload without throwing when an included
 * image or listing relationship is missing.
 *
 * @param {Object} sdkResponse
 * @returns {Array<Object>}
 */
export const denormaliseReviewQuery = sdkResponse => {
  const apiResponse = sdkResponse?.data;
  const data = apiResponse?.data;
  const resources = Array.isArray(data) ? data : data ? [data] : [];
  if (!resources.length) {
    return [];
  }
  const entities = updatedEntities({}, apiResponse);
  return denormalisedEntities(entities, resources, false);
};

const listingsImageQuery = () => ({
  include: ['images', 'author', 'author.profileImage'],
  'fields.image': [
    'variants.square-small',
    'variants.square-small2x',
    'variants.listing-card',
    'variants.listing-card-2x',
    ...IMAGE_FIELD_VARIANTS,
  ],
  ...createImageVariantConfig('square-small', 96, 1),
  ...createImageVariantConfig('square-small2x', 192, 1),
  ...createImageVariantConfig('listing-card', 400, 1),
  ...createImageVariantConfig('listing-card-2x', 800, 1),
});

const looksLikeScreenName = title => {
  const parts = trimmed(title).split(/\s+/);
  if (parts.length < 2 || trimmed(title).length > 48) {
    return false;
  }
  return parts.every(part => /^[A-ZÀ-ÖØ-Þ]/.test(part));
};

const pickAuthorListing = listings => {
  const published = (Array.isArray(listings) ? listings : []).filter(
    listing => !listing?.attributes?.deleted && listing?.attributes?.state === 'published'
  );
  const titled = published.filter(listing => trimmed(listing?.attributes?.title));
  const named = titled.find(listing => looksLikeScreenName(listing.attributes.title));
  return named || titled[0] || published[0] || (Array.isArray(listings) ? listings[0] : null) || null;
};

/**
 * Fetch each reviewer's published listing when the review payload has no
 * listing title or photo (talker-authored ofProvider reviews).
 *
 * @param {Object} sdk
 * @param {Array<Object>} [reviews]
 * @returns {Promise<Array<Object>>}
 */
export const attachAuthorProfileListings = async (sdk, reviews = []) => {
  const list = Array.isArray(reviews) ? reviews : [];
  if (!sdk?.listings?.query || list.length === 0) {
    return list.map(hydrateReviewAuthor);
  }
  const needed = [];
  const seen = new Set();
  list.forEach(review => {
    const authorId = entityId(review?.author?.id);
    if (!authorId || seen.has(authorId)) {
      return;
    }
    seen.add(authorId);
    needed.push(authorId);
  });
  if (needed.length === 0) {
    return list.map(hydrateReviewAuthor);
  }
  const listingsByAuthor = new Map();
  await Promise.all(
    needed.map(authorId =>
      sdk.listings
        .query({
          author_id: authorId,
          ...listingsImageQuery(),
          per_page: 8,
        })
        .then(response => {
          const listings = denormaliseReviewQuery(response);
          const picked = pickAuthorListing(listings);
          if (picked) {
            listingsByAuthor.set(authorId, picked);
          }
        })
        .catch(() => {})
    )
  );
  return list.map(review => {
    const authorId = entityId(review?.author?.id);
    const profileListing = listingsByAuthor.get(authorId);
    if (!profileListing) {
      return hydrateReviewAuthor(review);
    }
    return hydrateReviewAuthor({
      ...review,
      authorListing: profileListing,
      author: {
        ...(review.author || {}),
        profileListing,
        profileImage: review.author?.profileImage || profileListing.author?.profileImage,
      },
    });
  });
};
